import {
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import {
  BillingMethod,
  BillingRate,
  BillingRateParameterType,
  BillingRuleType,
  GetCustomerDetailsQuery,
} from "../../../graphql/generated";
import billingMethodLabel from "../../../utils/labels/billingRule/billingMethodLabel";
import { isRateTaxable } from "../../../utils/billingRule/isRateTaxable";
import LocaleProvider from "../../../providers/LocaleProvider";
import { useTranslation } from "react-i18next";
import { sortBy } from "lodash";
import RateParameterForm from "./RateParameterForm";
import { testIds } from "./RateForm.testIds";

type RateFormProps = {
  rate: BillingRate;
  onChange: (billingRate: BillingRate) => void;
  ruleType: BillingRuleType;
  compact?: boolean;
  showChargeLabel?: boolean;
  showTaxable?: boolean;
  customer?: GetCustomerDetailsQuery["customerById"];
};
const billingMethodOrder = [
  BillingMethod.FlatFee,
  BillingMethod.PerMile,
  BillingMethod.PerEmptyMile,
  BillingMethod.PerMileWithDeadhead,
  BillingMethod.PerKm,
  BillingMethod.PerEmptyKm,
  BillingMethod.PerKmWithDeadhead,
  BillingMethod.PerUnit,
  BillingMethod.PerKg,
  BillingMethod.PerPound,
  BillingMethod.PerLiter,
  BillingMethod.PerGallon,
];

const rateParametersPerBillingMethod: Partial<
  Record<BillingMethod, BillingRateParameterType[]>
> = {
  [BillingMethod.PerUnit]: [
    BillingRateParameterType.GoodProfile,
    BillingRateParameterType.QuantityType,
  ],
  [BillingMethod.PerKg]: [
    BillingRateParameterType.GoodProfile,
    BillingRateParameterType.QuantityType,
  ],
  [BillingMethod.PerPound]: [
    BillingRateParameterType.GoodProfile,
    BillingRateParameterType.QuantityType,
  ],
  [BillingMethod.PerLiter]: [
    BillingRateParameterType.GoodProfile,
    BillingRateParameterType.QuantityType,
    BillingRateParameterType.StorageFacility,
  ],
  [BillingMethod.PerGallon]: [
    BillingRateParameterType.GoodProfile,
    BillingRateParameterType.QuantityType,
    BillingRateParameterType.StorageFacility,
  ],
  [BillingMethod.PerLocation]: [
    BillingRateParameterType.LocationType,
    BillingRateParameterType.FreeLocationCount,
  ],
};

const RateForm = ({
  rate,
  onChange,
  ruleType,
  customer,
  compact = false,
  showChargeLabel = false,
  showTaxable = true,
}: RateFormProps) => {
  const { t } = useTranslation("billingRules");

  return (
    <Grid container direction="row" alignItems="center" spacing={1} flex={1}>
      <Grid item xs={12} md={compact ? 8 : 6} lg={compact ? 6 : 4}>
        <Select data-testid={testIds.rateFormBillingMethodSelector}
          label="Billing method"
          size="small"
          value={rate.method}
          onChange={(event) => {
            const billingMethod = event.target.value as BillingMethod;
            const rateParameterTypes =
              rateParametersPerBillingMethod[billingMethod] || [];
            onChange({
              ...rate,
              method: billingMethod,
              parameters: rateParameterTypes.map((type) => ({
                type,
                value:
                  rate.parameters?.find((p) => p.type === type)?.value || null,
              })),
            });
          }}
          sx={{ display: "flex" }}
        >
          {sortBy(Object.values(BillingMethod), (method) =>
            billingMethodOrder.indexOf(method) === -1
              ? Infinity
              : billingMethodOrder.indexOf(method)
          )
            .filter((method) => {
              if (ruleType === BillingRuleType.Primary) {
                return (
                  method !== BillingMethod.PercentageOfLineHaul &&
                  method !== BillingMethod.PerDetentionHour &&
                  method !== BillingMethod.PercentageOfTotal
                );
              }
              return true;
            })
            .map((billingMethod) => (
              <MenuItem data-testid={`${testIds.rateFormBillingMethodOption}-${billingMethod}`} key={billingMethod} value={billingMethod}>
                <ListItemText primary={billingMethodLabel(billingMethod)} />
              </MenuItem>
            ))}
        </Select>
        {rate.parameters?.length ? (
          <Stack
            spacing={1}
            sx={{
              mt: 1,
            }}
          >
            {rate.parameters?.map((parameter, index) => (
              <Stack direction="column">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <RateParameterForm
                    key={`condition-${index}`}
                    rateParameter={parameter}
                    customer={customer}
                    onChange={(rateParameter) =>
                      onChange({
                        ...rate,
                        parameters: rate.parameters?.map((p, i) =>
                          i === index ? rateParameter : p
                        ),
                      })
                    }
                  />
                </Stack>
              </Stack>
            ))}
          </Stack>
        ) : null}
      </Grid>
      <Grid item xs={12} md={compact ? 4 : 4} lg={compact ? 6 : 3}>
        <TextField
          type="number"
          value={String(rate.amount) || ""}
          onChange={(event) => {
            onChange({
              ...rate,
              amount: parseFloat(event.target.value) || 0,
            });
          }}
          size="small"
          label={t("form.amount")}
          inputProps={{ "data-testid": testIds.rateFormAmount,
            type: "number",
            min: 0,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {rate.method === BillingMethod.PercentageOfLineHaul ||
                rate.method === BillingMethod.PercentageOfTotal
                  ? "%"
                  : LocaleProvider.getCurrencySymbol()}
              </InputAdornment>
            ),
          }}
          sx={{ display: "flex" }}
        />
      </Grid>
      {(!compact || showChargeLabel) && (
        <Grid item xs={12} md={compact ? 12 : 4} lg={compact ? 12 : 2}>
          <Stack spacing={1}>
            <TextField
              type="string"
              value={rate.label || ""}
              onChange={(event) => {
                onChange({
                  ...rate,
                  label: event.target.value,
                });
              }}
              size="small"
              label={t("form.chargeLabel", "Charge label")}
              sx={{ display: "flex" }}
              inputProps={
                {
                  "data-testid": testIds.rateFormChargeLabel,
                } as React.InputHTMLAttributes<HTMLInputElement>
              }
            />
            {!compact && (
              <TextField
                type="string"
                value={rate.referenceNumber || ""}
                onChange={(event) => {
                  onChange({
                    ...rate,
                    referenceNumber: event.target.value,
                  });
                }}
                size="small"
                label={t("form.reference")}
                sx={{ display: "flex" }}
                inputProps={
                  {
                    "data-testid": testIds.rateFormReference,
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
            )}
          </Stack>
        </Grid>
      )}
      {showTaxable && (
        <Grid item xs={12} md={compact ? 12 : "auto"}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={isRateTaxable(rate, ruleType === BillingRuleType.Tax)}
                onChange={(event, checked) => {
                  onChange({
                    ...rate,
                    taxable: checked,
                  });
                }}
                inputProps={
                  {
                    "data-testid": testIds.rateFormTaxable,
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
            }
            label={t("form.taxable", "Taxable")}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default RateForm;
